import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Globe, TerminalSquare, Wrench } from "lucide-react";
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { CopyButton } from "@/client/components/copy-button";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { RedfishEndpointCard } from "@/client/features/bmc/redfish-endpoint-card";
import { PolicyPanel, RedfishExplorer } from "@/client/features/bmc/bmc-staff-panels";
import { type Node, nodesApi } from "@/client/features/nodes/nodes-api";
import { ticketsApi } from "@/client/features/tickets/tickets-api";
import { type NodeTabContext, useNodeTab } from "@/client/pages/customer/node-tab-context";

// xterm.js is only needed on a node's console, so load it on demand rather than
// carrying it in the main bundle.
const SerialConsoleCard = lazy(() =>
  import("@/client/features/bmc/serial-console-card").then((m) => ({
    default: m.SerialConsoleCard,
  })),
);
import { cn } from "@/client/lib/utils";
import { ApiError } from "@/client/lib/api";
import { formatPorts, parsePortList } from "@/shared/ports";

// Mirror the list view's status-to-tone mapping so a node reads the same in both
// places; unknown statuses fall back to a neutral outline.
function statusVariant(status: string | null): "secondary" | "outline" | "destructive" | "warning" {
  switch (status) {
    case "production":
    case "active":
      return "secondary";
    case "maintenance":
      return "warning";
    case "offline":
    case "failed":
    case "decommissioning":
      return "destructive";
    default:
      return "outline";
  }
}

function Field({
  label,
  value,
  copyable,
  mono,
}: {
  label: string;
  value: string | null;
  copyable?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className={cn("text-sm", mono && "font-mono break-all")}>
        {value ? (
          copyable ? (
            <span className="flex items-center gap-1">
              <span>{value}</span>
              <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} className="size-6" />
            </span>
          ) : (
            value
          )
        ) : (
          "—"
        )}
      </dd>
    </div>
  );
}

// Per-node actions that land in later phases. Shown disabled so the surface is
// discoverable now and each feature slots into its own row when its backend is
// ready. (Public IP and Power graduated to live sections.)
const UPCOMING_ACTIONS = [
  {
    icon: TerminalSquare,
    title: "Custom boot flow",
    description: "Boot this node from your own custom PXE flow.",
    cta: "Enable",
  },
] as const;

// Matches the textarea styling used on the Support ticket form so the request
// dialog reads consistently (there is no shared Textarea component yet).
const TEXTAREA_CLASS =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const requestSchema = z
  .object({
    ports: z.string().min(1, "Enter at least one port."),
    note: z.string().trim().max(2000, "Keep the note under 2000 characters.").optional(),
  })
  .superRefine((val, ctx) => {
    const parsed = parsePortList(val.ports);
    if (!parsed.ok) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: parsed.reason, path: ["ports"] });
    }
  });

type RequestValues = z.infer<typeof requestSchema>;

// A public IP + port forwarding is provisioned out of band by Mantle (PE-933),
// so the portal collects the request and files it as a support ticket instead
// of provisioning directly. On success we hand the customer straight to the
// ticket thread so they can track it. The node is identified to staff by its
// internal IP only - the Mantle node name is never surfaced (PE-914).
function RequestPublicAccessDialog({ node, trigger }: { node: Node; trigger: ReactNode }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const hasIp = Boolean(node.publicIpAddress);
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      ports: node.publicPorts.length > 0 ? formatPorts(node.publicPorts) : "",
      note: "",
    },
  });

  // The dialog lives on the always-mounted card, so the form would otherwise
  // retain edits from a previous (unsubmitted) open. Reset to the node's current
  // NetBox ports each time it opens so a "Request a change" never describes a
  // stale port list.
  const onOpenChange = (next: boolean) => {
    if (next) {
      form.reset({
        ports: node.publicPorts.length > 0 ? formatPorts(node.publicPorts) : "",
        note: "",
      });
    }
    setOpen(next);
  };

  const onSubmit = async (values: RequestValues) => {
    const nodeRef = node.internalIp ?? "an unidentified node";
    const note = values.note?.trim();
    const description =
      `Requesting public access for a node.\n\n` +
      `Node internal IP: ${node.internalIp ?? "unknown"}\n` +
      `Requested ports: ${values.ports.trim()}\n` +
      (hasIp
        ? `Currently assigned public IP: ${node.publicIpAddress}\n`
        : `No public IP currently assigned.\n`) +
      (note ? `\nNotes:\n${note}\n` : "");
    try {
      const ticket = await ticketsApi.create({
        title: `Public access request for ${nodeRef}`,
        description,
        priority: "normal",
      });
      toast.success(`Request ${ticket.identifier} submitted.`);
      setOpen(false);
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't submit the request. Try again.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasIp ? "Request a public access change" : "Request public access"}
          </DialogTitle>
          <DialogDescription>
            Public IPs and port forwarding are set up by the Mantle team. Tell us which ports you
            need and we'll open a support request you can track.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="ports"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ports</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="80, 443, 8000-9000"
                      className="font-mono"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Single ports or ranges, comma-separated (e.g. 80, 443, 8000-9000).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note (optional)</FormLabel>
                  <FormControl>
                    <textarea
                      className={TEXTAREA_CLASS}
                      placeholder="Anything else that helps us set this up."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Submitting…" : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Read-only public access, driven by NetBox (PE-933): the assigned public IP and
// its forwarded ports, shown only when both are present. This is the customer's
// authoritative view. Requesting/changing is ticket-based and offered to tenant
// admins entitled to the publicPorts feature (`canRequest`).
function PublicAccessCard({ node, canRequest }: { node: Node; canRequest: boolean }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Globe className="text-muted-foreground size-4" />
          <CardTitle>Public access</CardTitle>
        </div>
        <CardDescription>
          The public IP and forwarded ports Mantle has assigned to this node, reachable only from
          the source ranges on your Network access list.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {node.publicIpAddress ? (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-0.5">
              <dt className="text-muted-foreground text-xs">Public IP</dt>
              <dd className="flex items-center gap-1 font-mono text-sm break-all">
                <span>{node.publicIpAddress}</span>
                <CopyButton
                  value={node.publicIpAddress}
                  label="Copy public IP"
                  className="size-6"
                />
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-muted-foreground text-xs">Forwarded ports</dt>
              <dd className="flex items-center gap-1 font-mono text-sm break-all">
                <span>{formatPorts(node.publicPorts)}</span>
                <CopyButton
                  value={formatPorts(node.publicPorts)}
                  label="Copy forwarded ports"
                  className="size-6"
                />
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-muted-foreground text-sm">No public IP assigned to this node yet.</p>
        )}
        {canRequest ? (
          <RequestPublicAccessDialog
            node={node}
            trigger={
              <Button
                size="sm"
                variant={node.publicIpAddress ? "outline" : "default"}
                className="w-fit"
              >
                {node.publicIpAddress ? "Request a change" : "Request public access"}
              </Button>
            }
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

type DetailState = "loading" | "ready" | "notfound" | "error";

function NodeTabLink({ to, end, label }: { to: string; end?: boolean; label: string }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "border-primary text-foreground"
            : "text-muted-foreground hover:text-foreground border-transparent",
        )
      }
    >
      {label}
    </NavLink>
  );
}

export function NodeDetailPage() {
  const { id = "" } = useParams();
  // Remount per node id so navigating between nodes (even within the same tab)
  // starts from a clean load instead of briefly showing the previous node - and
  // resets any per-node tab state (e.g. an open console session).
  return <NodeLayout key={id} id={id} />;
}

function NodeLayout({ id }: { id: string }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const features = useAuthStore((s) => s.features);
  const impersonation = useAuthStore((s) => s.impersonation);
  const isAdmin = user?.kind === "customer" && user.tenantRole === "tenant_admin";
  // Mantle staff viewing as a tenant ("view as") may run power actions and see
  // the staff-only debug tabs; the server enforces both.
  const isStaffView = impersonation !== null;
  const [node, setNode] = useState<Node | null>(null);
  const [state, setState] = useState<DetailState>("loading");

  const requestRef = useRef(0);

  const load = useCallback(async () => {
    // Guard against out-of-order responses: a retry starts a newer request, and
    // a slower earlier response must not overwrite the node or flip its state.
    const reqId = ++requestRef.current;
    setState("loading");
    setNode(null);
    try {
      const result = await nodesApi.get(id);
      if (requestRef.current !== reqId) return;
      setNode(result);
      setState("ready");
    } catch (err) {
      if (requestRef.current !== reqId) return;
      if (err instanceof ApiError && err.status === 404) {
        setState("notfound");
      } else {
        setState("error");
        toast.error(err instanceof ApiError ? err.message : "Failed to load this node.");
      }
    }
  }, [id]);

  // Refresh just the node (after a public-IP request/withdraw) without flipping
  // the whole view back to its loading skeleton.
  const refreshPublicIp = useCallback(async () => {
    const reqId = requestRef.current;
    try {
      const result = await nodesApi.get(id);
      if (requestRef.current !== reqId) return;
      setNode(result);
    } catch {
      if (requestRef.current !== reqId) return;
      toast.warning("Saved, but couldn't refresh the page. Reload to see the latest.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === "notfound") {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" className="w-fit" onClick={() => navigate("/nodes")}>
          <ArrowLeft /> Back to nodes
        </Button>
        <p className="text-muted-foreground text-sm">
          This node doesn't exist or isn't allocated to your organization.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Button variant="ghost" className="w-fit" onClick={() => navigate("/nodes")}>
        <ArrowLeft /> Back to nodes
      </Button>

      {state === "error" ? (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">
            Couldn't load this node. Refresh to try again.
          </p>
          <Button variant="outline" className="w-fit" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : state === "loading" || !node ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-xl font-semibold tracking-tight break-all">
              {node.internalIp ?? "Node"}
            </h1>
            <Badge variant={statusVariant(node.status)}>
              {node.statusLabel ?? node.status ?? "—"}
            </Badge>
          </div>

          <div className="border-b">
            <nav className="-mb-px flex gap-1 overflow-x-auto">
              <NodeTabLink to={`/nodes/${node.id}`} end label="Overview" />
              {features.bmc ? (
                <NodeTabLink to={`/nodes/${node.id}/console`} label="Console & Power" />
              ) : null}
              {features.bmc && isStaffView ? (
                <>
                  <NodeTabLink to={`/nodes/${node.id}/redfish`} label="Redfish" />
                  <NodeTabLink to={`/nodes/${node.id}/policy`} label="Policy" />
                </>
              ) : null}
            </nav>
          </div>

          <Outlet
            context={{ node, isAdmin, isStaffView, refreshPublicIp } satisfies NodeTabContext}
          />
        </>
      )}
    </div>
  );
}

export function NodeOverviewTab() {
  const { node, isAdmin, isStaffView } = useNodeTab();
  // Requesting a public IP is a tenant-admin action gated by the publicPorts
  // entitlement; the read-only NetBox display above is always shown. It also
  // needs ticketing configured (the request files a ticket, which 503s when
  // ticketing is off) and is hidden during a read-only staff "view as" session,
  // where submitting would hit the impersonation guard's 403.
  const publicPortsEnabled = useAuthStore((s) => s.features.publicPorts);
  const ticketsEnabled = useAuthStore((s) => s.features.tickets);
  const showMoreActions = false;
  return (
    <div className="flex flex-col gap-6">
      {node.status === "maintenance" ? (
        <div className="flex items-start gap-3 rounded-lg border border-[color-mix(in_srgb,var(--warning-solid)_35%,var(--border))] bg-[var(--warning-bg)] px-4 py-3 text-sm text-[var(--warning-fg)]">
          <Wrench className="mt-0.5 size-4 shrink-0" />
          <div>
            This node is temporarily in maintenance. It stays assigned to your organization and
            reappears as available once Mantle completes the work.
          </div>
        </div>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>
            Connect to this node at its internal IP. Name it however you like in your own DNS.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
            <Field label="Internal IP" value={node.internalIp} copyable mono />
            <Field label="GPU type" value={node.gpuType} />
            <Field label="Serial" value={node.serial} copyable />
            <Field label="Status" value={node.statusLabel ?? node.status} />
            <Field label="Site" value={node.site} />
            <Field label="Rack" value={node.rack} />
          </dl>
        </CardContent>
      </Card>

      <PublicAccessCard
        node={node}
        canRequest={isAdmin && publicPortsEnabled && ticketsEnabled && !isStaffView}
      />

      {showMoreActions ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>More actions</CardTitle>
              <Badge variant="outline" className="font-normal">
                Coming soon
              </Badge>
            </div>
            <CardDescription>
              These controls aren't available yet. They'll light up here as each one ships.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y">
              {UPCOMING_ACTIONS.map(({ icon: Icon, title, description, cta }) => (
                <li
                  key={title}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-start gap-3">
                    <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{title}</p>
                      <p className="text-muted-foreground text-xs">{description}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled>
                    {cta}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export function NodeConsolePowerTab() {
  const { node, isAdmin, isStaffView } = useNodeTab();
  const features = useAuthStore((s) => s.features);
  // Direct-URL guard: the tab is hidden without the feature, but a pasted link
  // shouldn't reach it either. Fall back to the node's Overview.
  if (!features.bmc) {
    return <Navigate to={`/nodes/${node.id}`} replace />;
  }
  const canControl = isAdmin || isStaffView;
  // Console-primary, full width: the console spans the whole width so a firmware
  // menu gets all the columns it needs, with the power controls folded into the
  // same panel beneath the terminal, and the Redfish endpoint below that (PE-937).
  return (
    <div className="flex flex-col gap-6">
      <Suspense fallback={<Skeleton className="h-[520px] w-full" />}>
        <SerialConsoleCard
          nodeId={node.id}
          canControl={canControl}
          maintenanceLocked={node.status === "maintenance" && !isStaffView}
        />
      </Suspense>
      {/* Token create/revoke is read-only during a staff "view as", so only a
          real tenant admin sees the create-token link. */}
      <RedfishEndpointCard nodeId={node.id} canManageTokens={isAdmin && !isStaffView} />
    </div>
  );
}

export function NodeRedfishTab() {
  const { node, isStaffView } = useNodeTab();
  const features = useAuthStore((s) => s.features);
  if (!features.bmc || !isStaffView) {
    return <Navigate to={`/nodes/${node.id}`} replace />;
  }
  return <RedfishExplorer nodeId={node.id} />;
}

export function NodePolicyTab() {
  const { node, isStaffView } = useNodeTab();
  const features = useAuthStore((s) => s.features);
  if (!features.bmc || !isStaffView) {
    return <Navigate to={`/nodes/${node.id}`} replace />;
  }
  return <PolicyPanel nodeId={node.id} />;
}
